import { useEffect, useRef, useCallback, useState } from "react";

export type IdleState = "active" | "idle" | "warning";

interface UseIdleTimerOptions {
  /** How long before the badge flips to "Idle" (default 5s) */
  badgeIdleMs?: number;
  /** Total idle timeout until logout (default 5 min) */
  idleMs?: number;
  /** Show warning countdown this many ms before logout (default 10s) */
  warningMs?: number;
  onIdle?: () => void;
  onActive?: () => void;
  onWarning?: () => void;
  /** Called when the countdown reaches 0 — should trigger logout */
  onTimeout?: () => void;
}

interface UseIdleTimerReturn {
  state: IdleState;
  /** Seconds remaining when in "warning" state */
  countdown: number;
  reset: () => void;
}

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
  "visibilitychange",
] as const;

export function useIdleTimer({
  badgeIdleMs = 5_000,
  idleMs = 5 * 60 * 1000,
  warningMs = 10_000,
  onIdle,
  onActive,
  onWarning,
  onTimeout,
}: UseIdleTimerOptions = {}): UseIdleTimerReturn {
  const [state, setState] = useState<IdleState>("active");
  const [countdown, setCountdown] = useState(0);

  const badgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stateRef = useRef<IdleState>("active");

  const clearAllTimers = useCallback(() => {
    if (badgeTimerRef.current) clearTimeout(badgeTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }, []);

  const startCountdown = useCallback(() => {
    const seconds = Math.ceil(warningMs / 1000);
    setCountdown(seconds);

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    logoutTimerRef.current = setTimeout(() => {
      onTimeout?.();
    }, warningMs);
  }, [warningMs, onTimeout]);

  const reset = useCallback(() => {
    clearAllTimers();

    if (stateRef.current !== "active") {
      stateRef.current = "active";
      setState("active");
      setCountdown(0);
      onActive?.();
    }

    // Badge flips to "idle" after badgeIdleMs of inactivity
    badgeTimerRef.current = setTimeout(() => {
      if (stateRef.current === "active") {
        stateRef.current = "idle";
        setState("idle");
        onIdle?.();
      }
    }, badgeIdleMs);

    // Warning dialog fires at (idleMs - warningMs)
    const timeUntilWarning = idleMs - warningMs;
    warningTimerRef.current = setTimeout(() => {
      stateRef.current = "warning";
      setState("warning");
      onWarning?.();
      startCountdown();
    }, timeUntilWarning);
  }, [clearAllTimers, badgeIdleMs, idleMs, warningMs, onActive, onIdle, onWarning, startCountdown]);

  useEffect(() => {
    const handleActivity = () => reset();

    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, handleActivity, { passive: true })
    );

    reset();

    return () => {
      ACTIVITY_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, handleActivity)
      );
      clearAllTimers();
    };
  }, [reset, clearAllTimers]);

  return { state, countdown, reset };
}
